import React, { useState } from "react";
import { Box, Button } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AuthPageLayout from "../../components/layout/AuthPageLayout";
import { Form } from "../../components/Form";
import FormPhoneInput from "../../components/Form/FormPhoneInput";
import FormFieldErrors from "../../components/Form/FormFieldErrors";
import {
  useFormContext,
  FormDataType,
  FormFieldValue,
} from "../../components/Form/FormContext";
import { ROUTES } from "../../constants";
import {
  validatePhoneNumber,
  cleanPhoneNumber,
} from "../../utils/phoneValidation";
import { ValidationResult } from "../../hooks/useFormValidation";
import images from "../../assets";
import {
  TwoFactorContainer,
  TwoFactorIcon,
  TwoFactorTitle,
  TwoFactorSubtitle,
  TwoFactorActions,
  SmsConsentText,
  SmsConsentLinks,
  SmsConsentLink,
} from "../Auth/TwoFactorAuthPage/TwoFactorAuthPage.styled";
import api from "../../api/axios";

export const DEMO_PHONE_KEY = "demo_phone_number";

const API_BASE = process.env.REACT_APP_API_BASE_URL ?? "";

interface DemoPhoneFormProps {
  apiErrors: string[];
  fieldErrors: { [k: string]: string[] };
  isSubmitting: boolean;
}

const DemoPhoneForm: React.FC<DemoPhoneFormProps> = ({
  isSubmitting,
  apiErrors,
  fieldErrors,
}) => {
  const { t } = useTranslation();
  const { validationResult } = useFormContext();

  const isFormValid =
    !!validationResult?.isValid &&
    Array.isArray(validationResult?.errorMessages) &&
    validationResult.errorMessages.length === 0;

  return (
    <AuthPageLayout withHeaderImage>
      <TwoFactorContainer>
        <TwoFactorIcon>
          <Box
            component="img"
            src={images.lockedDisplay}
            alt=""
            sx={{ width: "156px" }}
          />
        </TwoFactorIcon>

        <TwoFactorTitle>
          {t("twoFactor.title")}
        </TwoFactorTitle>

        <TwoFactorSubtitle>{t("twoFactor.subtitle")}</TwoFactorSubtitle>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "12px",
            width: "100%",
          }}
        >
          <FormPhoneInput
            name="phoneNumber"
            label={t("twoFactor.phoneNumber", "Phone Number")}
            placeholder={t(
              "twoFactor.phoneNumberPlaceholder",
              "+1 (___) ___-____",
            )}
            required
            fullWidth
          />

          <FormFieldErrors apiErrors={apiErrors} fieldErrors={fieldErrors} />

          <TwoFactorActions>
            <Button
              type="submit"
              variant="contained"
              disabled={isSubmitting || !isFormValid}
              fullWidth
            >
              {isSubmitting
                ? t("twoFactor.sending", "Sending...")
                : t("twoFactor.sendCode", "Send Code")}
            </Button>
          </TwoFactorActions>

          <SmsConsentText>{t("twoFactor.smsConsent")}</SmsConsentText>

          <SmsConsentLinks>
            <SmsConsentLink
              href={ROUTES.TERMS}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t("twoFactor.termsOfUse")}
            </SmsConsentLink>
            <SmsConsentLink
              href={ROUTES.PRIVACY}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t("twoFactor.privacyPolicy")}
            </SmsConsentLink>
            <SmsConsentLink
              href={ROUTES.SMS_POLICY}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t("twoFactor.smsMessagingPolicy")}
            </SmsConsentLink>
          </SmsConsentLinks>
        </Box>
      </TwoFactorContainer>
    </AuthPageLayout>
  );
};

const DemoPhonePage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiErrors, setApiErrors] = useState<string[]>([]);

  const validationRules = {
    phoneNumber: {
      custom: (value: FormFieldValue) => {
        const phoneValue = String(value || "");
        if (!phoneValue) {
          return t("twoFactor.validation.required", "Phone number is required");
        }
        if (!validatePhoneNumber(phoneValue)) {
          return t(
            "twoFactor.validation.invalidFormat",
            "Please enter a valid US phone number",
          );
        }
        return "";
      },
    },
  };

  const handleSubmit = async (
    formData: FormDataType,
    validationResult: ValidationResult,
  ) => {
    const hasClientErrors =
      !validationResult?.isValid ||
      (Array.isArray(validationResult?.errorMessages) &&
        validationResult.errorMessages.length > 0);
    if (hasClientErrors) return;

    setApiErrors([]);
    setIsSubmitting(true);

    try {
      const phoneNumber = cleanPhoneNumber(String(formData.phoneNumber));
      sessionStorage.setItem(DEMO_PHONE_KEY, phoneNumber);

      await api.post(`${API_BASE.replace(/\/$/, "")}/testing/sms`, null, {
        params: { phoneNumber },
      });

      navigate(ROUTES.DEMO_VERIFY);
    } catch {
      setApiErrors([
        t(
          "demo.phone.sendError",
          "Failed to send verification code. Please try again.",
        ),
      ]);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form
      validationRules={validationRules}
      onSubmit={handleSubmit}
      externalErrors={{}}
      showFieldErrors={false}
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "12px",
        width: "100%",
      }}
    >
      <DemoPhoneForm
        isSubmitting={isSubmitting}
        apiErrors={apiErrors}
        fieldErrors={{}}
      />
    </Form>
  );
};

export default DemoPhonePage;
